import {DataScope} from '@sollapay/enums';
import {useCallback} from 'react';

import {useUserAccessQuery} from './useUserAccess';

import type {Permission} from '@sollapay/enums';

export interface PermissionsState {
  /** Whether the current role holds this permission at all. */
  hasPermission: (permission: Permission) => boolean;
  /** `firm` means the user reads accounts they don't own; `own` restricts them to their own. */
  readScope: DataScope;
  /** `null` means the role holds no write permission anywhere. */
  writeScope: DataScope | null;
  isLoading: boolean;
}

/**
 * Resolves the authenticated user's RBAC permissions from `/user/me`. Holding a permission is
 * not enough to write to a trust account — per-account flags come from `TrustAccountProvider`
 * (or `getTrustAccountRowPermissions` for list rows), which read the account's `allowedActions`.
 */
export function usePermissions(): PermissionsState {
  const {data, isLoading} = useUserAccessQuery();

  const permissions = data?.permissions;

  const hasPermission = useCallback(
    (permission: Permission): boolean => permissions?.includes(permission) ?? false,
    [permissions]
  );

  return {
    hasPermission,
    readScope: data?.readScope ?? DataScope.OWN,
    writeScope: data?.writeScope ?? null,
    isLoading
  };
}
